// src/components/layout/PublicLayout.jsx
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useCart } from '@/context/CartContext'
import { Search, ShoppingCart, User, Menu, X, ChevronRight } from 'lucide-react'
import CurrencySwitcher from '@/components/common/CurrencySwitcher'

export default function PublicLayout() {
  const { user, logout } = useAuth()
  const { count } = useCart()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()

  const handleSearch = e => {
    e.preventDefault()
    if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`)
  }

  const dashboardRoute = user?.role === 'admin' ? '/admin' : user?.role === 'supplier' ? '/supplier' : '/buyer'

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Topbar */}
      <header className="border-b border-border sticky top-0 z-40 bg-white">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="/images/logo.png" alt="Fabrinza" className="h-9" onError={e => e.target.style.display='none'} />
            <span className="font-syne font-extrabold text-xl text-gradient">Fabrinza</span>
          </Link>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-lg mx-4">
            <div className="relative w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search fabrics, suppliers…"
                className="input pl-9 pr-4 h-10 rounded-full"
              />
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <CurrencySwitcher />
            <NavLink to="/buyer/cart" className="relative text-muted hover:text-pink">
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-pink rounded-full text-white text-[10px] font-extrabold flex items-center justify-center">
                  {count > 9 ? '9+' : count}
                </span>
              )}
            </NavLink>
            {user ? (
              <Link to={dashboardRoute} className="btn-primary py-2 px-4 text-xs">
                <User size={14} /> My Account
              </Link>
            ) : (
              <>
                <Link to="/login"    className="btn-secondary py-2 px-4 text-xs hidden sm:flex">Sign In</Link>
                <Link to="/register" className="btn-primary  py-2 px-4 text-xs">Join Free</Link>
              </>
            )}
            <button className="md:hidden text-muted" onClick={() => setMobileOpen(v => !v)}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Nav bar */}
        <nav className="hidden md:block border-t border-border">
          <div className="max-w-7xl mx-auto px-4 flex gap-6 h-10 items-center">
            {[
              { to: '/fabrics',   label: 'All Fabrics' },
              { to: '/suppliers', label: 'Suppliers' },
              { to: '/gi-marketplace', label: 'GI Premium' },
              { to: '/fabrics?category=cotton',  label: 'Cotton' },
              { to: '/fabrics?category=silk',    label: 'Silk' },
              { to: '/fabrics?category=linen',   label: 'Linen' },
              { to: '/fabrics?category=synthetic', label: 'Synthetic' },
              { to: '/textile-index', label: 'Textile Index' },
              { to: '/blog',      label: 'Fabric Guide' },
              { to: '/roadmap',   label: '🗺 Roadmap' },
            ].map(l => (
              <NavLink key={l.to} to={l.to}
                className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? 'text-pink' : 'text-muted hover:text-gray-900'}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-white px-4 py-4 space-y-3">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search fabrics…" className="input pl-9 h-10 rounded-full" />
              </div>
            </form>
            {['/fabrics', '/suppliers', '/blog'].map(to => (
              <Link key={to} to={to} onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between py-2 border-b border-border text-sm font-medium">
                {to.slice(1).replace('-', ' ')} <ChevronRight size={16} className="text-muted" />
              </Link>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-surface border-t border-border mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <div className="font-syne font-extrabold text-lg text-gradient mb-3">Fabrinza</div>
            <p className="text-sm text-muted leading-relaxed">India's smart textile marketplace — connecting buyers and suppliers since 2025.</p>
          </div>
          {[
            { title: 'Shop',    links: ['All Fabrics', 'Cotton', 'Silk', 'Linen', 'Synthetic'] },
            { title: 'Sell',    links: ['Become a Supplier', 'Pricing Plans', 'Supplier Guide', 'Bulk Import'] },
            { title: 'Company', links: ['About Us', 'Blog', 'Contact', 'Privacy Policy', 'Terms'] },
          ].map(col => (
            <div key={col.title}>
              <div className="font-bold text-sm mb-3">{col.title}</div>
              <ul className="space-y-2">
                {col.links.map(l => <li key={l}><a href="#" className="text-sm text-muted hover:text-pink transition-colors">{l}</a></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-border text-center py-4 text-xs text-muted">
          © {new Date().getFullYear()} Fabrinza. All rights reserved.
        </div>
      </footer>
    </div>
  )
}
